/**
 * Pill Marquee — zwei gegenläufige Endlos-Bänder aus Stichwort-Pills.
 *
 * Hochmoderner Allround-Filler & Übergang: zwei Reihen aus Glas-Pills
 * (Kategorien, USPs, Keywords) laufen gegenläufig durch — oben nach links,
 * unten nach rechts — mit weichen Fade-Rändern. Optional eine Headline in der
 * Mitte. Sehr gut für Stories, Hooks und als Atemszene zwischen Inhalten.
 *
 * Inputs:
 *   - items[]  → Pill-Texte. Format je Pill: "Text" oder "Text | iconKey".
 *   - text     → optionale, mittig eingeblendete Headline (über den Bändern).
 *
 * Technik: kein `repeat:-1` (würde die endliche, frameweise gesuchte Timeline
 * sprengen). Stattdessen wird der Inhalt mehrfach dupliziert und über die
 * gesamte Clip-Dauer linear verschoben — dadurch nahtloser Lauf ohne Sprung.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, gsapSetLine,
  makeTl, typeSize, weights, withAlpha, colors as themeColors,
} from "../../reel/sceneShared.js";
import { lucideIcon, splitItem } from "./_mockHelpers.js";

export const pillMarquee: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const violet = themeColors.secondary;
  const pink = themeColors.accent;
  const palette = [accent, violet, pink];
  const isVertical = ctx.format === "9:16";

  const headline = inputs.text ?? "";
  const baseItems = inputs.items?.length
    ? inputs.items
    : [
        "Restaurants | shop", "Shops | store", "Taxis | taxi", "Hotels | hotel",
        "Events | event", "Dienstleister | service", "Lokal kaufen | heart",
        "0 % Provision | euro", "Verifiziert | shield", "Sofort buchen | bolt",
      ];

  const sm = typeSize("sm", ctx.format);
  const lg = typeSize("lg", ctx.format);
  const pillPx = isVertical ? sm * 0.66 : sm * 0.7;
  const iconPx = Math.round(pillPx * 1.05);
  const pillPadY = isVertical ? 16 : 20;
  const pillPadX = isVertical ? 26 : 32;

  const wrapId = `${clipPrefix}-wrap`;
  const headId = `${clipPrefix}-head`;
  const rowAId = `${clipPrefix}-rowA`;
  const rowBId = `${clipPrefix}-rowB`;

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "allyver", accent });

  function pill(raw: string, i: number): string {
    const [label, iconRaw] = splitItem(raw);
    const text = (label ?? "").trim();
    const icon = (iconRaw ?? "").trim();
    const color = palette[i % palette.length] ?? accent;
    return `<div style="display:inline-flex;align-items:center;gap:12px;padding:${pillPadY}px ${pillPadX}px;border-radius:9999px;white-space:nowrap;background:linear-gradient(180deg, ${withAlpha(themeColors.fg, 0.08)} 0%, ${withAlpha(themeColors.bg, 0.55)} 100%);border:1px solid ${withAlpha(color, 0.45)};box-shadow:0 10px 28px rgba(0,0,0,0.32), 0 0 22px ${withAlpha(color, 0.18)};color:${themeColors.fg};font-family:'Outfit',sans-serif;font-weight:${weights.semibold};font-size:${pillPx}px;letter-spacing:-0.3px;">
      ${icon ? `<span style="display:inline-flex;color:${color};">${lucideIcon(icon, iconPx, color, 2.2)}</span>` : `<span style="width:9px;height:9px;border-radius:9999px;background:${color};box-shadow:0 0 12px ${color};"></span>`}
      <span>${escapeHtml(text)}</span>
    </div>`;
  }

  // Genug Wiederholungen, dass das Band die ganze Clipdauer ohne Naht-Sprung füllt.
  const reps = 4;
  const rowA = Array.from({ length: reps }, () => baseItems).flat();
  const rowB = [...baseItems].reverse();
  const rowBFull = Array.from({ length: reps }, () => rowB).flat();

  const gap = isVertical ? 18 : 24;
  const rowStyle = `display:inline-flex;gap:${gap}px;align-items:center;will-change:transform;`;
  const trackWrap = `position:relative;width:100%;overflow:hidden;`;
  const fadeMask = "-webkit-mask-image:linear-gradient(90deg, transparent 0%, #000 12%, #000 88%, transparent 100%);mask-image:linear-gradient(90deg, transparent 0%, #000 12%, #000 88%, transparent 100%);";

  const headPx = isVertical ? lg * 0.86 : lg * 0.9;
  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:${isVertical ? 26 : 34}px;overflow:hidden;">
    <div style="${trackWrap}${fadeMask}">
      <div id="${rowAId}" style="${rowStyle}padding-left:40px;">${rowA.map((p, i) => pill(p, i)).join("")}</div>
    </div>
    ${headline ? `<div id="${headId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${headPx}px;line-height:1.05;letter-spacing:-1.6px;color:${themeColors.fg};text-align:center;text-wrap:balance;max-width:${isVertical ? 880 : 1300}px;padding:0 48px;text-shadow:0 0 50px ${withAlpha(accent, 0.4)};">${escapeHtml(headline)}</div>` : ""}
    <div style="${trackWrap}${fadeMask}">
      <div id="${rowBId}" style="${rowStyle}padding-left:40px;">${rowBFull.map((p, i) => pill(p, i)).join("")}</div>
    </div>
  </div>`;

  // Verschiebung: Band A nach links, Band B nach rechts (startet vorverschoben).
  // Strecke relativ zur Wiederholungslänge — eine „Periode" ≈ 1/reps der Breite.
  const drift = isVertical ? 26 : 20; // Prozent der eigenen Breite über die Clipdauer
  const init = [
    gsapSetLine(`#${rowAId}`, { xPercent: 0 }),
    gsapSetLine(`#${rowBId}`, { xPercent: -drift }),
    headline ? gsapSetLine(`#${headId}`, { opacity: 0, y: 18, scale: 0.96 }) : "",
  ].filter(Boolean).join("\n");

  const tweens: string[] = [
    t.to(`#${rowAId}`, { xPercent: -drift, duration: clipDurationSec, ease: "raw:\"none\"" }, 0),
    t.to(`#${rowBId}`, { xPercent: 0, duration: clipDurationSec, ease: "raw:\"none\"" }, 0),
  ];
  if (headline) {
    tweens.push(t.to(`#${headId}`, { opacity: 1, y: 0, scale: 1, duration: 0.6, ease: "raw:\"back.out(1.5)\"" }, 0.3));
  }
  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
